import { Immutable } from "immer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { Link } from "tsi-common-react/src/components/Link/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { BreakPoint } from "tsi-common-react/src/models/screen";
import { isMobile } from "tsi-common-react/src/utils/detectMobile";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconX from "../../assets/svg/x.svg";
import { IMenuItem } from "../../utils/menu.interface";
import { MenuLinks } from "./main-menu-links";
import { NavFooter } from "./main-menu-nav-footer";
import { PromoAd } from "./main-menu-promo-ad";
import { SearchNav } from "./main-menu-search-nav";

import siteNavStyles from "../../styles/composable/site-nav.module.scss";
import styles from "./index.module.scss";

type MainMenuProps = Immutable<{
    cmsData: IMenuItem[];
}>;

export const MainMenu: React.FC<MainMenuProps> = ({ cmsData }) => {
    const isSSR = useIsSSR();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(0);
    const modalIsOpenRef = useRef(modalIsOpen);
    modalIsOpenRef.current = modalIsOpen;

    useEffect(() => {
        const win = getWindow();
        if (!win) return;
        setWindowWidth(win.innerWidth);
        const handleResize = () => {
            setWindowWidth(win.innerWidth);
            if (win.innerWidth < BreakPoint.SMALL && isMobile.Android()) {
                return;
            }
            if (modalIsOpenRef.current) {
                setModalIsOpen(false);
            }
        };
        win.addEventListener("resize", handleResize);
        return () => {
            win.removeEventListener("resize", handleResize);
        };
    }, []);

    const openModal = (event?: React.MouseEvent<HTMLElement>) => {
        let menuLinkElem = null;
        if (event) {
            event.preventDefault();
            menuLinkElem = event.currentTarget;
        }
        if (!isSSR && menuLinkElem) {
            const document = getDocument();
            if (
                document &&
                !menuLinkElem.classList.contains(
                    siteNavStyles.siteNavMainMenuItemMobile,
                )
            ) {
                document.body.classList.add("body-dropdown-open--desktop");
            }
        }
        setModalIsOpen(true);
    };

    const closeModal = (
        event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        let menuLinkElem: HTMLElement | null = null;
        if (event) {
            event.preventDefault();
            menuLinkElem = event.currentTarget as HTMLElement;
        }
        if (!isSSR && menuLinkElem) {
            const document = getDocument();
            if (
                document &&
                !menuLinkElem.classList.contains(
                    siteNavStyles.siteNavMainMenuItemMobile,
                )
            ) {
                document.body.classList.remove("body-dropdown-open--desktop");
            }
        }
        setModalIsOpen(false);
    };

    let contentPosition: React.CSSProperties["position"];
    let contentTop: React.CSSProperties["top"];
    let contentHeight: React.CSSProperties["height"];

    const isMobileView = windowWidth === 0 || windowWidth <= BreakPoint.SMALL;

    if (isMobileView) {
        contentPosition = "fixed";
        contentTop = 0;
        contentHeight = "100%";
    } else {
        contentPosition = "relative";
        contentTop = "104px";
        contentHeight = "auto";
    }
    const modalStyle: Modal.Styles = {
        overlay: {
            position: "absolute",
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            position: contentPosition,
            left: 0,
            right: 0,
            top: contentTop,
            width: "100%",
            height: contentHeight,
            overflow: "hidden",
            border: "none",
            boxShadow: "0 10px 10px -5px rgba(0,0,0,.3)",
            borderTop: "1px solid rgba(234, 234, 234, 1)",
        },
    };

    const { promoAds, navFooter } = useMemo(() => {
        const promoAds: React.ReactElement[] = [];
        const navFooter: React.ReactElement[] = [];
        cmsData.forEach((menuItem) => {
            if (menuItem.type !== "nav_category") {
                return;
            }
            if (menuItem.value.sub_nav.length > 0) {
                // Build out Nav Promo Ad (if it exists)
                menuItem.value.sub_nav.forEach((val, i) => {
                    switch (val.type) {
                        case "nav_advert":
                            promoAds.push(
                                <PromoAd promoArrayEl={val} key={i} />,
                            );
                            break;
                        case "nav_footer":
                            navFooter.push(
                                <NavFooter navFooterEl={val} key={i} />,
                            );
                            break;
                        default:
                            break;
                    }
                });
            }
        });
        return { promoAds, navFooter };
    }, [cmsData]);

    return (
        <span className={styles.siteNavMainMenuItemContainer}>
            <span>
                <span>
                    <Link
                        href={null}
                        title={t`Open Menu`}
                        aria-label={t`Open Menu`}
                        onClick={openModal}
                        className={concatClassNames([
                            siteNavStyles.siteNavMainMenuItem,
                            siteNavStyles.siteNavMainMenuItemMobile,
                        ])}
                    >
                        <SVGIcon
                            svgClassName={styles.siteNavMobileToggleIcon}
                            name="barsIcon"
                        />
                    </Link>
                </span>
                <MenuLinks
                    cmsData={cmsData}
                    isMobile={false}
                    modalIsOpen={modalIsOpen}
                    openModal={openModal}
                    closeModal={closeModal}
                />
            </span>
            <Modal
                contentLabel={t`Shop pillows menu`}
                ariaHideApp={false}
                aria={{ modal: true }}
                style={modalStyle}
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                onAfterOpen={() => {
                    if (!isSSR) {
                        focusElement(
                            `.${styles.mainDropdownMenu} .${styles.mainDropdownMenuClose}`,
                        );
                    }
                }}
            >
                <h2 className="ada-screenreader-only">{t`Shop Pillows Menu`}</h2>
                <div className={styles.mainDropdownMenu} aria-expanded="true">
                    <button
                        aria-label={t`Close Menu`}
                        onClick={closeModal}
                        className={concatClassNames([
                            styles.mainDropdownMenuMobile,
                            styles.mainDropdownMenuClose,
                        ])}
                    >
                        <SVG
                            aria-hidden="true"
                            className={styles.mainDropdownMenuCloseIcon}
                            src={iconX}
                        />
                    </button>
                    <div
                        className={concatClassNames([
                            styles.mainDropdownMenuContent,
                            "l-capped-width",
                        ])}
                        id="main-dropdown-menu-shop"
                    >
                        <MenuLinks
                            cmsData={cmsData}
                            isMobile={true}
                            modalIsOpen={modalIsOpen}
                            openModal={openModal}
                            closeModal={closeModal}
                        />
                        {promoAds}
                    </div>
                    <div
                        className={concatClassNames([
                            styles.mainDropdownMenuFooterWrapper,
                            "l-full-width",
                        ])}
                    >
                        <div
                            className={concatClassNames([
                                styles.mainDropdownMenuFooter,
                                "l-capped-width",
                            ])}
                        >
                            {navFooter}
                        </div>
                    </div>
                    <SearchNav />
                </div>
            </Modal>
        </span>
    );
};
